import { EJobAnalysisType, ELanguage, maxA, maxB, minA, minB } from '@/common'
export function rejectAfterDelay(ms: number) {
    return new Promise((_, reject) => {
        setTimeout(reject, ms, new Error('timeout'))
    })
}
export async function delay(ms: number) {
    return ms
        ? new Promise((resolve) => setTimeout(resolve, ms))
        : Promise.resolve()
}

export function decodeKeyword(encodedKeyword: string) {
    return !encodedKeyword?.length
        ? ''
        : encodedKeyword
              .replace(/-(sk|ik)$/, '')
              .split(/[-]{2,}/g)
              .join('[TEMP DOUBLE HYPHEN]')
              .split('-')
              .join(' ')
              .split('[TEMP DOUBLE HYPHEN]')
              .join(' - ')
              .replace(/\+\+/g, '_plus_plus')
              .replace(/C_plus_plus|c_plus_plus/, 'C++')
              .replace(/C_sharp|c_sharp/, 'C#')
              .replace(/%26/, '&')
}

/**
 * Clean slugs by removing spaces, invalid characters and dashes
 * @param slug - the slug you wanna clean
 */
export function slugify(slug: string = '') {
    slug = slug.replace(/^\s+|\s+$/g, '') // trim
    slug = slug.toLowerCase()

    // remove accents
    slug = transformToLatin(slug)

    slug = slug
        .replace(/[^a-z0-9 -]/g, '-') // invalid chars to -
        .replace(/\s+/g, '-') // collapse whitespace and replace by -
        .replace(/-+/g, '-') // collapse dashes

    return slug
}

/**
 * Transform all characters with accent into latin, e.g. `Ô kìa Con vịt` to `o kia con vit`
 * @param text - the text you want to transform
 */
export function transformToLatin(text: string) {
    text = text.toString().toLowerCase().trim()

    const sets = [
        { to: 'a', from: '[ÀÁÂÃÄÅÆĀĂĄẠẢẤẦẨẪẬẮẰẲẴẶἀ]' },
        { to: 'c', from: '[ÇĆĈČ]' },
        { to: 'd', from: '[ÐĎĐÞ]' },
        { to: 'e', from: '[ÈÉÊËĒĔĖĘĚẸẺẼẾỀỂỄỆ]' },
        { to: 'g', from: '[ĜĞĢǴ]' },
        { to: 'h', from: '[ĤḦ]' },
        { to: 'i', from: '[ÌÍÎÏĨĪĮİỈỊ]' },
        { to: 'j', from: '[Ĵ]' },
        { to: 'ij', from: '[Ĳ]' },
        { to: 'k', from: '[Ķ]' },
        { to: 'l', from: '[ĹĻĽŁ]' },
        { to: 'm', from: '[Ḿ]' },
        { to: 'n', from: '[ÑŃŅŇ]' },
        { to: 'o', from: '[ÒÓÔÕÖØŌŎŐỌỎỐỒỔỖỘỚỜỞỠỢǪǬƠ]' },
        { to: 'oe', from: '[Œ]' },
        { to: 'p', from: '[ṕ]' },
        { to: 'r', from: '[ŔŖŘ]' },
        { to: 's', from: '[ßŚŜŞŠȘ]' },
        { to: 't', from: '[ŢŤ]' },
        { to: 'u', from: '[ÙÚÛÜŨŪŬŮŰŲỤỦỨỪỬỮỰƯ]' },
        { to: 'w', from: '[ẂŴẀẄ]' },
        { to: 'x', from: '[ẍ]' },
        { to: 'y', from: '[ÝŶŸỲỴỶỸ]' },
        { to: 'z', from: '[ŹŻŽ]' },
        { to: '-', from: "[·/_,:;']" }
    ]

    sets.forEach((set) => {
        text = text.replace(new RegExp(set.from, 'gi'), set.to)
    })

    return text
}

/**
 * Just a helper function to help create an html object quickly whenever you need to use `dangerouslySetInnerHTML`
 * @param HTMLMarkup
 */
export function createMarkup(HTMLMarkup: string) {
    return { __html: HTMLMarkup }
}

/**
 * Return `true` if the screen is desktop: screen size >= 992
 */
export function isDesktop(): boolean {
    if (typeof window !== 'undefined') {
        // Note that using window.innerWidth will return double the value of the width on some iOS versions
        return document.documentElement.clientWidth >= 992
    }
    return false
}

/**
 * Return `true` if the screen is either phone or tablet: screen size < 992px
 */
export function isMobile(): boolean {
    if (typeof window !== 'undefined') {
        // Note that using window.innerWidth will return double the value of the width on some iOS versions
        return document.documentElement.clientWidth < 992
    }
    return false
}

/**
 * Return `true` if the screen is phone: screen size < 768px
 */
export function isPhone(): boolean {
    if (typeof window !== 'undefined') {
        // Note that using window.innerWidth will return double the value of the width on some iOS versions
        return document.documentElement.clientWidth <= 768
    }
    return false
}

/**
 * Return `true` if the screen is tablet: 768px <= screen size < 992px
 */
export function isTablet(): boolean {
    if (typeof window !== 'undefined') {
        // Note that using window.innerWidth will return double the value of the width on some iOS versions
        return (
            document.documentElement.clientWidth >= 768 &&
            document.documentElement.clientWidth < 992
        )
    }
    return false
}
// For IPv4
const addrToNumber = (addr: string) => {
    return addr
        .split('.')
        .reduce((acc, cur, i) => (acc += Number(cur) << ((3 - i) * 8)), 0)
}

const subnetMaskToNumber = (mask: any) => {
    return (0xffffffff << (32 - Number(mask))) & 0xffffffff
}

const isIPv4FromGoogle = (userIP: string): boolean => {
    const googleBotIPRange = [
        '66.249.64.0/27',
        '66.249.64.128/27',
        '66.249.64.160/27',
        '66.249.64.192/27',
        '66.249.64.224/27',
        '66.249.64.32/27',
        '66.249.64.64/27',
        '66.249.64.96/27',
        '66.249.65.0/27',
        '66.249.65.128/27',
        '66.249.65.160/27',
        '66.249.65.192/27',
        '66.249.65.224/27',
        '66.249.65.32/27',
        '66.249.65.64/27',
        '66.249.65.96/27',
        '66.249.66.0/27',
        '66.249.66.128/27',
        '66.249.66.192/27',
        '66.249.66.32/27',
        '66.249.66.64/27',
        '66.249.68.0/27',
        '66.249.68.32/27',
        '66.249.68.64/27',
        '66.249.69.0/27',
        '66.249.69.128/27',
        '66.249.69.160/27',
        '66.249.69.192/27',
        '66.249.69.224/27',
        '66.249.69.32/27',
        '66.249.69.64/27',
        '66.249.69.96/27',
        '66.249.70.0/27',
        '66.249.70.128/27',
        '66.249.70.160/27',
        '66.249.70.192/27',
        '66.249.70.224/27',
        '66.249.70.32/27',
        '66.249.70.64/27',
        '66.249.70.96/27',
        '66.249.71.0/27',
        '66.249.71.128/27',
        '66.249.71.160/27',
        '66.249.71.192/27',
        '66.249.71.32/27',
        '66.249.71.64/27',
        '66.249.71.96/27',
        '66.249.72.0/27',
        '66.249.72.128/27',
        '66.249.72.160/27',
        '66.249.72.192/27',
        '66.249.72.224/27',
        '66.249.72.32/27',
        '66.249.72.64/27',
        '66.249.72.96/27',
        '66.249.73.0/27',
        '66.249.73.128/27',
        '66.249.73.160/27',
        '66.249.73.192/27',
        '66.249.73.224/27',
        '66.249.73.32/27',
        '66.249.73.64/27',
        '66.249.73.96/27',
        '66.249.74.0/27',
        '66.249.74.32/27',
        '66.249.74.64/27',
        '66.249.74.96/27',
        '66.249.75.0/27',
        '66.249.75.128/27',
        '66.249.75.160/27',
        '66.249.75.192/27',
        '66.249.75.224/27',
        '66.249.75.32/27',
        '66.249.75.64/27',
        '66.249.75.96/27',
        '66.249.76.0/27',
        '66.249.76.128/27',
        '66.249.76.160/27',
        '66.249.76.192/27',
        '66.249.76.224/27',
        '66.249.76.32/27',
        '66.249.76.64/27',
        '66.249.76.96/27',
        '66.249.77.0/27',
        '66.249.77.128/27',
        '66.249.77.32/27',
        '66.249.77.64/27',
        '66.249.77.96/27',
        '66.249.79.0/27',
        '66.249.79.128/27',
        '66.249.79.160/27',
        '66.249.79.192/27',
        '66.249.79.224/27',
        '66.249.79.32/27',
        '66.249.79.64/27',
        '66.249.79.96/27'
    ]
    let isFromGoogle = false
    // Loop through all CIDR from google IPv4 to find match IP
    googleBotIPRange.map((cidr: string) => {
        const [networkAddr, subnetMask] = cidr.split('/')
        const match =
            (addrToNumber(userIP) & subnetMaskToNumber(subnetMask)) ==
            addrToNumber(networkAddr)
        if (match) {
            isFromGoogle = true
        }
    })

    return isFromGoogle
}
// For IPv6
const isIPv6FromGoogle = (_userIP: string): boolean => {
    let isFromGoogle = false
    // TODO: Loop through all CIDR from google IPv6 to find match IP
    // IPv6 is not support yet
    return isFromGoogle
}

/**
 * Check user IP address if it is a bot or not
 * @param userIP string
 */
export const isIPFromGoogleBot = (userIP: string[]): boolean => {
    if (userIP.length === 0) {
        return false
    }
    let isFromGoogle = false
    for (let i = 0; i < userIP.length; i++) {
        if (!userIP[i]) {
            continue
        }
        const isIPv4 = userIP[i].indexOf('.') > 0
        const userIPSplitted = isIPv4
            ? userIP[i].split('.')
            : userIP[i].split(':')

        if (
            isIPv4 &&
            userIPSplitted[0] === '66' &&
            userIPSplitted[1] === '249'
        ) {
            isFromGoogle = isIPv4FromGoogle(userIP[i])
        } else if (
            !isIPv4 &&
            userIPSplitted.length > 1 &&
            userIPSplitted[0] === '2001' &&
            userIPSplitted[1] === '4860' &&
            userIPSplitted[2] === '4801'
        ) {
            isFromGoogle = isIPv6FromGoogle(userIP[i])
        }
    }
    return isFromGoogle
}

/**
 * Check a user-agent if it's a robot
 * @param userAgent
 */
export const isRobot = (userAgent: string, userIP: string[]): boolean => {
    const botUserAgents = [
        'googlebot',
        'google-structured-data-testing-tool',
        'bingbot',
        'linkedinbot',
        'mediapartners-google',
        'apis-google',
        'feedfetcher-google',
        'googlebot-image',
        'storebot-google'
    ]

    const currentUserAgent: string = userAgent.toLowerCase()

    let isBot = botUserAgents.some((item) => {
        return currentUserAgent.includes(item)
    })

    // Detect IP from Google bot
    // Only call detect IP when isBot = false (For better performance)
    if (!isBot && userIP !== undefined) {
        isBot = isIPFromGoogleBot(userIP)
    }

    return isBot
}
export function addCommaToNumber(number: number): string {
    const nums = number.toString().replace(/,/g, '')
    if (!nums || nums.endsWith('.')) return number.toString()
    return parseFloat(nums).toLocaleString('en-US')
}

/**
 * We need to cut off numbers in VND Currency or even in those positions having salary out of average ( from 10k USD ) since it causes a long text to display
 *
 * @param salary
 */
export function handleClippingSalary(salary: string) {
    return salary.substr(0, salary.indexOf(','))
}

/**
 * Round salary number
 *
 * @param number
 */
export function handleRound1Decimal(number: string): string {
    if (!number) return number
    return (Math.round(parseFloat(number) * 10) / 10).toString()
}

export function isVietnamese(text: string) {
    return /[\u0300-\u036f]|[đĐ]/gi.test(text.normalize('NFD'))
}

export function createTranslator<T extends Record<string, string>>({
    translations,
    defaultLanguage,
    currentLanguage
}: {
    translations: T
    defaultLanguage?: ELanguage
    currentLanguage: ELanguage
}): (str: keyof T, replacement?: Record<string, any>) => string {
    let firstEntry = null

    let detectedDefaultLanguage = defaultLanguage
    if (!detectedDefaultLanguage) {
        for (const [keyText, valueText] of Object.entries(translations)) {
            if (!firstEntry) {
                firstEntry = [keyText, valueText]
            }
            if (isVietnamese(keyText)) {
                detectedDefaultLanguage = ELanguage.VI
                break
            }
            if (isVietnamese(valueText)) {
                detectedDefaultLanguage = ELanguage.EN
                break
            }
        }
    }
    if (!detectedDefaultLanguage) {
        console.warn(
            `Cannot detect default language from provided dictionary. First entry is "${firstEntry}"`
        )
        console.warn(`Fallback to English as default language`)
        detectedDefaultLanguage = ELanguage.EN
    }
    return (text, replacement?) => {
        if (!text) {
            return ''
        }
        let matchedText =
            currentLanguage === detectedDefaultLanguage
                ? text
                : translations[text]
        if (!matchedText) {
            console.warn(`Translation for ${text as string} not found`)
            matchedText = text
        }
        return replacement
            ? Object.keys(replacement).reduce(
                  (acc: string, key) =>
                      acc.replace(
                          new RegExp(key, 'g'),
                          replacement[key].toString() as string
                      ),
                  matchedText as string
              )
            : (matchedText as string)
    }
}

export function VNWDecryptToken(token: string, key: string) {
    const decodedString = Buffer.from(token, 'base64').toString('binary')
    let result = ''

    for (let i = 1; i <= decodedString.length; i++) {
        const char = decodedString.at(i - 1)

        const keychar = key.at((i % key.length) - 1)

        const decryptedChar = String.fromCharCode(
            char!.charCodeAt(0) - keychar!.charCodeAt(0)
        )
        result += decryptedChar
    }
    return result
}

export function convertValueToNumber(value: any) {
    if (isNaN(Number(value))) {
        return 0
    }
    return Number(value)
}

export const convertDateTime = (time: number) => {
    const date = new Date(time)
    const day = date.getDate()
    const month = date.getMonth() + 1
    const year = date.getFullYear()

    const addZero = (date: number) => String(date).padStart(2, '0')
    // 30/10/2022
    const formatDate = addZero(day) + '/' + addZero(month) + '/' + year
    return formatDate
}

export function getPathFromUrl(url: string) {
    return url.split(/[?#]/)[0]
}

export function objToQueryStr(params: { [key: string]: string | number }) {
    if (typeof params !== 'object') {
        return
    }
    return Object.keys(params)
        .map((key) => `${key}=${encodeURIComponent(params[key])}`)
        .join('&')
}

export function parseJSON<T>(value?: string) {
    try {
        if (!value) return
        const parsedObject: T = JSON.parse(decodeURIComponent(value))
        return parsedObject
    } catch (error) {
        if (error instanceof SyntaxError) {
            // Handle SyntaxError specifically, like logging or returning a default value
            console.error('Invalid JSON format:', error.message)
            return // Or any other appropriate default value
        } else {
            // Handle other potential errors
            console.error('Error parsing JSON:', error)
            throw error // Re-throw the error for further handling
        }
    }
}

function getRandomInt(min: number, max: number) {
    min = Math.ceil(min)
    max = Math.floor(max)
    return Math.floor(Math.random() * (max - min + 1)) + min
}

/**
 * VNW-29492 A/B testing Job analysis
 *
 * 50% section see pattern A
 * 0% section see pattern B
 * 50% section see pattern C
 *
 * @author Thien Tran
 * @since Mar 18, 2024
 */
export function randomUserJobAnalysisType(): EJobAnalysisType {
    // Random from 1% to 100%
    const randomPercent = getRandomInt(1, 100)

    if (randomPercent >= minA && randomPercent <= maxA) {
        // Case A: 0% to 50%
        return EJobAnalysisType.A
    } else if (randomPercent >= minB && randomPercent <= maxB) {
        // Case B: 0% to 0%
        return EJobAnalysisType.B
    }
    return EJobAnalysisType.NONE
}

export function randomSmartNaviVersion() {
    /**
     * 10% v6
     * 90% v2
     */
    const v6Percent = Number(
        process.env.NEXT_PUBLIC_AB_SMART_NAVI_PERCENTAGE ?? '90'
    )
    // Random from 1% to 100%
    const randomPercent = getRandomInt(1, 100)

    return randomPercent >= v6Percent ? 'v6' : 'v2'
}

export function getISO8601WithOffset(date: Date) {
    // Get the individual components of the date
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0') // Months are zero-based
    const day = String(date.getDate()).padStart(2, '0')
    const hours = String(date.getHours()).padStart(2, '0')
    const minutes = String(date.getMinutes()).padStart(2, '0')
    const seconds = String(date.getSeconds()).padStart(2, '0')
    const offsetHours = String(-date.getTimezoneOffset() / 60).padStart(2, '0')
    const offsetMinutes = String(
        Math.abs(date.getTimezoneOffset() % 60)
    ).padStart(2, '0')

    // Determine the sign of the time zone offset
    const offsetSign = date.getTimezoneOffset() < 0 ? '+' : '-'

    // Construct the ISO 8601 string with the time zone offset
    const isoString = `${year}-${month}-${day}T${hours}:${minutes}:${seconds}${offsetSign}${offsetHours}:${offsetMinutes}`

    return isoString
}

export function uniqueArray<T>(array: T[]) {
    return array.filter((value, index, self) => self.indexOf(value) === index)
}
export function uniqueArrayObject<T>(array: T[], keyCompare: keyof T) {
    return array.filter(
        (tag, index, self) =>
            index === self.findIndex((t) => t[keyCompare] === tag[keyCompare])
    )
}

export async function decryptAICVHash(hash: string) {
    const textToDecrypt = decodeURIComponent(encodeURIComponent(hash))
    const hashKey = process.env.NEXT_PUBLIC_AI_RESUME_REVIEW_HASH_KEY || ''

    if (!hashKey) {
        throw new Error('Encryption key is missing')
    }

    // Ensure the hash key and IV are properly encoded and converted
    const keyBuffer = new TextEncoder().encode(hashKey.padEnd(32).slice(0, 32)) // Ensure 256 bits (32 bytes)
    const ivBuffer = new TextEncoder().encode(hashKey.padEnd(16).slice(0, 16)) // Ensure 128 bits (16 bytes)
    const encryptedBuffer = Uint8Array.from(
        atob(textToDecrypt.replace(/_/g, '/').replace(/-/g, '+')), // convert from base64 url base to standar base64
        (c) => c.charCodeAt(0)
    )

    const cryptoKey = await crypto.subtle.importKey(
        'raw',
        keyBuffer,
        { name: 'AES-CTR' },
        false,
        ['decrypt']
    )

    const decryptedBuffer = await crypto.subtle.decrypt(
        { name: 'AES-CTR', counter: ivBuffer, length: 64 },
        cryptoKey,
        encryptedBuffer
    )

    return new TextDecoder().decode(decryptedBuffer)
}

export function getValueByKey(array: string[], key: string) {
    const formattedArray = array.map((item) => item.split('='))
    
    const filteredValues = formattedArray.filter((item) => item[0] === key)
    if (filteredValues.length === 0) {
        return null
    }
    const keyValuePair = filteredValues[0]

    return keyValuePair.length > 1 ? keyValuePair[1] : null 
}

/**
 * Checks if an IP address is private (supports both IPv4 and IPv6)
 * @param ip - The IP address to check
 * @returns boolean - true if the IP is private, false otherwise
 * @throws Error if the IP address format is invalid
 */
const testingIPs = process.env.NEXT_PUBLIC_AI_RESUME_INTERNAL_TESTING_IP || ''
export function isPrivateIP(ip: string): boolean {
    // IPv6 check
    if (ip.includes(':')) {
        // IPv6 patterns
        const ipv6Patterns = [
            /^::1$/, // Loopback
            /^fc00::/, // Unique local address (fc00::/7)
            /^fd00::/, // Unique local address (fd00::/8)
            /^fe80::/, // Link-local address
            /^::/, // Unspecified address
            /^2001:db8:/ // Documentation address
        ]

        return ipv6Patterns.some((pattern) => pattern.test(ip))
    }

    // IPv4 check
    const ipv4Regex = /^(\d{1,3}\.){3}\d{1,3}$/

    if (!ipv4Regex.test(ip)) {
        throw new Error('Invalid IP address format')
    }

    const testingIPList = testingIPs.split(',').filter(item => item)

    // enable to all user
    if (testingIPList.length === 0) {
        return true
    }

    return testingIPList.some((internalIP) => ip.startsWith(internalIP))
}

export function capitalizeText(text: string) {
    return text.charAt(0).toUpperCase() + text.slice(1)
}
